import { useMemo, useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { AdminTabShell } from './AdminTabShell';
import {
  useAdminNotificationEligibility,
  type PropertyEligibilityRow,
} from '@/hooks/admin/useAdminNotificationEligibility';

/**
 * Read-only admin view of notification eligibility for one org. Server-derived by
 * the canonical classifier (admin-notification-eligibility edge function). No repair
 * buttons. Hidden/incomplete systems remain visible but are clearly marked ineligible.
 * Counts reconcile exactly with the classifier output (same rows).
 */

const BUCKET_LABELS: Record<string, string> = {
  eligible_healthy: 'Healthy',
  eligible_verified_low_battery: 'Verified low battery',
  provider_incident_active: 'Provider incident',
  telemetry_stale: 'Telemetry stale',
  zero_soc_unverified: 'Zero SOC (unverified)',
  battery_not_present: 'No battery',
  battery_telemetry_unsupported: 'Battery telemetry unsupported',
  onboarding_incomplete: 'Onboarding incomplete',
  hidden: 'Hidden',
  never_operational: 'Never operational',
  forecast_ineligible: 'Forecast ineligible',
  configuration_required: 'Configuration required',
  manual_review: 'Manual review',
};

const INELIGIBLE_BUCKETS = new Set([
  'hidden', 'onboarding_incomplete', 'never_operational', 'provider_incident_active',
  'telemetry_stale', 'battery_not_present', 'battery_telemetry_unsupported', 'zero_soc_unverified',
]);

function bucketClass(bucket: string): string {
  if (bucket === 'eligible_healthy') return 'bg-emerald-500/15 text-emerald-300';
  if (bucket === 'eligible_verified_low_battery') return 'bg-amber-500/15 text-amber-300';
  if (bucket === 'manual_review' || bucket === 'configuration_required') return 'bg-blue-500/15 text-blue-300';
  return 'bg-red-500/15 text-red-300';
}

export function NotificationEligibilityTab({ orgId: orgIdProp = null }: { orgId?: string | null } = {}) {
  // Org-scoped: each fetch returns exactly one org (server enforces super-admin + scope).
  const [orgInput, setOrgInput] = useState<string>(orgIdProp ?? '');
  const [activeOrg, setActiveOrg] = useState<string | null>(orgIdProp);
  const { data, isLoading, error } = useAdminNotificationEligibility(activeOrg, !!activeOrg);
  const [bucketFilter, setBucketFilter] = useState<string>('all');
  const [providerFilter, setProviderFilter] = useState<string>('all');

  const rows = data?.properties ?? [];

  const counts = useMemo(() => {
    const m = new Map<string, number>();
    for (const r of rows) m.set(r.primary_bucket, (m.get(r.primary_bucket) ?? 0) + 1);
    return [...m.entries()].sort((a, b) => b[1] - a[1]);
  }, [rows]);

  const providers = useMemo(
    () => [...new Set(rows.map((r) => r.provider).filter(Boolean))] as string[],
    [rows],
  );

  const filtered = useMemo(
    () => rows.filter((r) =>
      (bucketFilter === 'all' || r.primary_bucket === bucketFilter) &&
      (providerFilter === 'all' || r.provider === providerFilter)),
    [rows, bucketFilter, providerFilter],
  );

  return (
    <AdminTabShell isLoading={isLoading && !!activeOrg} error={error as Error | null}>
      <div className="space-y-4">
        {/* Explicit org scope (required). Super-admin can inspect any single org. */}
        <div className="flex items-center gap-2">
          <input
            className="w-96 rounded border border-border bg-background px-2 py-1 text-sm"
            placeholder="Organization ID (explicit tenant scope)"
            value={orgInput}
            onChange={(e) => setOrgInput(e.target.value)}
          />
          <button
            className="rounded bg-primary px-3 py-1 text-sm text-primary-foreground"
            onClick={() => setActiveOrg(orgInput.trim() || null)}
          >
            Classify
          </button>
          {data && (
            <span className="text-xs text-muted-foreground">
              classifier {new Date(data.org_id ? (data as unknown as { classifier_at?: string }).classifier_at ?? '' : '').toLocaleTimeString?.() ?? ''} · read-only
            </span>
          )}
        </div>
        {!activeOrg && <p className="text-sm text-muted-foreground">Enter an organization ID and click Classify.</p>}

        {/* Bucket summary — reconciles with classifier output */}
        <div className="flex flex-wrap gap-2">
          <Badge
            className="cursor-pointer bg-muted text-foreground"
            onClick={() => setBucketFilter('all')}
          >
            All {rows.length}
          </Badge>
          {counts.map(([bucket, n]) => (
            <Badge
              key={bucket}
              className={`cursor-pointer ${bucketClass(bucket)}`}
              onClick={() => setBucketFilter(bucket)}
            >
              {BUCKET_LABELS[bucket] ?? bucket} {n}
            </Badge>
          ))}
        </div>

        {providers.length > 1 && (
          <div className="flex flex-wrap gap-2 text-xs">
            <button className={`px-2 py-1 rounded ${providerFilter === 'all' ? 'bg-primary text-primary-foreground' : 'bg-muted'}`} onClick={() => setProviderFilter('all')}>all providers</button>
            {providers.map((p) => (
              <button key={p} className={`px-2 py-1 rounded ${providerFilter === p ? 'bg-primary text-primary-foreground' : 'bg-muted'}`} onClick={() => setProviderFilter(p)}>{p}</button>
            ))}
          </div>
        )}

        <div className="overflow-x-auto rounded-lg border border-border">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 text-left text-xs uppercase text-muted-foreground">
              <tr>
                <th className="px-3 py-2">Property</th>
                <th className="px-3 py-2">Provider</th>
                <th className="px-3 py-2">Bucket</th>
                <th className="px-3 py-2">Eligibility</th>
                <th className="px-3 py-2">SOC validity</th>
                <th className="px-3 py-2">Battery</th>
                <th className="px-3 py-2">Active alerts</th>
                <th className="px-3 py-2">Last delivered</th>
                <th className="px-3 py-2">Reason codes</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((r: PropertyEligibilityRow) => (
                <tr key={r.property_id} className="border-t border-border/50">
                  <td className="px-3 py-2">
                    {r.property_name ?? r.property_id.slice(0, 8)}
                    {r.is_hidden && <span className="ml-1 text-[10px] text-red-400">hidden</span>}
                    {!r.onboarding_complete && <span className="ml-1 text-[10px] text-amber-400">incomplete</span>}
                  </td>
                  <td className="px-3 py-2 text-muted-foreground">{r.provider ?? '—'}</td>
                  <td className="px-3 py-2"><Badge className={bucketClass(r.primary_bucket)}>{BUCKET_LABELS[r.primary_bucket] ?? r.primary_bucket}</Badge></td>
                  <td className="px-3 py-2">
                    {r.customer_ineligible || INELIGIBLE_BUCKETS.has(r.primary_bucket)
                      ? <span className="text-red-400">ineligible</span>
                      : <span className="text-emerald-400">eligible{r.battery_severity ? ` (${r.battery_severity})` : ''}</span>}
                  </td>
                  <td className="px-3 py-2 text-muted-foreground">{r.soc_validity}{r.latest_soc != null ? ` (${r.latest_soc}%)` : ''}</td>
                  <td className="px-3 py-2 text-muted-foreground">{r.battery_presence}{r.battery_capacity_resolved ? '' : ' · cap?'}</td>
                  <td className="px-3 py-2">{r.active_alert_states.length > 0 ? r.active_alert_states.map((s) => s.event_kind).join(', ') : '—'}</td>
                  <td className="px-3 py-2 text-muted-foreground">{r.last_delivered_notification ? `${r.last_delivered_notification.event_kind}` : 'never'}</td>
                  <td className="px-3 py-2 text-[10px] text-muted-foreground">{r.secondary_reason_codes.join(', ') || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="text-xs text-muted-foreground">
          Read-only. Server-derived by the canonical classifier. Repair is performed separately via the reviewed dry-run plan.
        </p>
      </div>
    </AdminTabShell>
  );
}
